import { useState, useEffect } from 'react'
import Papa from 'papaparse'
import { detectCsvEncoding } from '../../utils/csvEncoding'
import { useParams, useNavigate } from 'react-router-dom'
import { useMatches } from '../../hooks/useMatches'
import { useTournaments } from '../../hooks/useTournaments'
import { useTeams } from '../../hooks/useTeams'
import { usePoules } from '../../hooks/usePoules'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { Button, Modal, Input, Badge, DarkModeToggleCompact, CardHeader } from '../../components/ui'
import { Calendar, Plus, Trash2, ArrowLeft, Edit2, Sparkles, Zap, MapPin, User, Clock, Check, FileText, Download, FileUp, Upload } from 'lucide-react'
import { HelpTip } from '../../components/ui/HelpTip'
import { TeamAvatar } from '../../components/ui/TeamAvatar'
import { soundFx } from '../../lib/soundEffects'
import { generateRoundRobinSchedule, generateDoubleRoundRobin } from '../../utils/matchScheduler'
import { generateSmartSchedule } from '../../utils/smartScheduler'
import { AIMatchReportGenerator } from '../../components/tools/AIMatchReportGenerator'
import { askGemini } from '../../lib/gemini'
import { validateTournament } from '../../lib/tournamentValidation'
import { SetScoreInput } from '../../components/matches/SetScoreInput'

import { useTranslation } from 'react-i18next'

export const MatchesPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { tournamentId } = useParams()
  const { activeTenant, refreshTenants } = useTenant()
  const { tournaments } = useTournaments() // Fetch tournaments to check sport_type
  const { matches, setMatches, loading, createMatch, updateMatch, updateScore, deleteMatch, refetch } = useMatches(tournamentId)
  const { teams } = useTeams(tournamentId)
  const { poules } = usePoules(tournamentId)

  // Derive sport_type & tournament_type
  const currentTournament = tournaments.find(t => t.id === tournamentId)
  const sportType = currentTournament?.sport_type || 'goals'

  // Smart detection: Explicitly knockout OR (no type defined AND no poules exist)
  // This failsafe ensures that if a knockout tournament was created without a type label, it still shows the bracket button instead of the broken poule generator.
  const isKnockout = currentTournament?.type === 'knockout' || (!currentTournament?.type && poules.length === 0)

  const [showModal, setShowModal] = useState(false)
  const [showGeneratorModal, setShowGeneratorModal] = useState(false)
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [editingMatch, setEditingMatch] = useState(null)
  const [editingDetails, setEditingDetails] = useState(null)
  const [aiReportMatch, setAiReportMatch] = useState(null)
  const [scores, setScores] = useState({})
  const [showSetsTable, setShowSetsTable] = useState({})
  const [newMatch, setNewMatch] = useState({
    home_team_id: '',
    away_team_id: '',
    poule_id: '',
    referee: '',
    field: '',
    match_time: '',
    match_date: ''
  })
  const [generatorStep, setGeneratorStep] = useState(1)
  const [previewMatches, setPreviewMatches] = useState([])
  const [generatorSettings, setGeneratorSettings] = useState({
    poule_ids: [],
    type: 'single',
    generating: false,
    start_time: '09:00',
    match_duration: 20,
    break_duration: 5,
    selected_fields: []
  })
  const [newFieldInput, setNewFieldInput] = useState('')
  const [isAddingField, setIsAddingField] = useState(false)
  const [selectedPouleFilter, setSelectedPouleFilter] = useState('all') // Poule filter

  // Validation Status
  const [setupStatus, setSetupStatus] = useState({ isReady: true })
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    const checkSetup = async () => {
      if (tournamentId) {
        const status = await validateTournament(tournamentId)
        setSetupStatus(status)
      }
    }
    checkSetup()
  }, [tournamentId, showModal, showGeneratorModal]) // Re-check when modals close (user might have fixed it elsewhere)

  const handleAddNewField = async () => {
    if (!newFieldInput.trim()) return

    try {
      const currentFields = activeTenant.fields || []
      if (currentFields.includes(newFieldInput)) {
        alert(t('matches_page.alert_field_exists'))
        return
      }

      const updatedFields = [...currentFields, newFieldInput]

      const { error } = await supabase
        .from('tenants')
        .update({ fields: updatedFields })
        .eq('id', activeTenant.id)

      if (error) throw error

      await refreshTenants()

      // Auto-select
      setGeneratorSettings(prev => ({
        ...prev,
        selected_fields: [...prev.selected_fields, newFieldInput]
      }))

      setNewFieldInput('')
      setIsAddingField(false)
      soundFx.play('click')

    } catch (err) {
      alert(`${t('matches_page.alert_field_error')}: ` + err.message)
    }
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    try {
      await createMatch(newMatch)
      soundFx.play('success')
      setShowModal(false)
      setNewMatch({
        home_team_id: '',
        away_team_id: '',
        poule_id: '',
        referee: '',
        field: '',
        match_time: '',
        match_date: ''
      })
    } catch (err) {
      alert('Fout: ' + err.message)
    }
  }

  const handleScoreUpdate = async (matchId) => {
    const scoreData = scores[matchId]
    if (!scoreData) return

    if (sportType === 'sets') {
      let validSets = []
      if (Array.isArray(scoreData.set_scores)) {
        validSets = scoreData.set_scores
      } else if (Array.isArray(scoreData.sets)) {
        const sets = scoreData.sets
        validSets = sets.map(s => {
          const homeVal = (s.home === '' || s.home === undefined || s.home === null) ? null : parseInt(s.home)
          const awayVal = (s.away === '' || s.away === undefined || s.away === null) ? null : parseInt(s.away)
          return {
            home: isNaN(homeVal) ? null : homeVal,
            away: isNaN(awayVal) ? null : awayVal
          }
        }).filter(s => s.home !== null && s.away !== null)
      }

      let homeWins = null
      let awayWins = null

      if (validSets.length > 0) {
        homeWins = 0
        awayWins = 0
        validSets.forEach(s => {
          if (s.home > s.away) homeWins++
          if (s.away > s.home) awayWins++
        })
      } else {
        const homeVal = (scoreData.home === '' || scoreData.home === null || scoreData.home === undefined) ? null : parseInt(scoreData.home)
        const awayVal = (scoreData.away === '' || scoreData.away === null || scoreData.away === undefined) ? null : parseInt(scoreData.away)
        homeWins = isNaN(homeVal) ? null : homeVal
        awayWins = isNaN(awayVal) ? null : awayVal
      }

      const previousMatches = matches
      setMatches(matches.map(m => m.id === matchId ? { ...m, set_scores: validSets, score_home: homeWins, score_away: awayWins } : m))
      setEditingMatch(null)
      setScores({})

      try {
        await updateMatch(matchId, {
          set_scores: validSets,
          score_home: homeWins,
          score_away: awayWins
        })
      } catch (err) {
        alert('Fout: ' + err.message)
        setMatches(previousMatches)
      }
    } else {
      const homeVal = scoreData.home === '' ? null : parseInt(scoreData.home)
      const awayVal = scoreData.away === '' ? null : parseInt(scoreData.away)

      const previousMatches = matches
      setMatches(matches.map(m =>
        m.id === matchId
          ? { ...m, score_home: homeVal, score_away: awayVal }
          : m
      ))
      setEditingMatch(null)
      setScores({})

      try {
        await updateScore(matchId, homeVal, awayVal)
      } catch (err) {
        alert('Fout: ' + err.message)
        setMatches(previousMatches)
      }
    }
  }

  const startEditing = (match) => {
    setEditingMatch(match.id)

    const loadedSets = Array.isArray(match.set_scores) && match.set_scores.length > 0
      ? match.set_scores
      : []

    const editableSets = loadedSets.map(s => ({
      home: s.home !== null && s.home !== undefined ? String(s.home) : '',
      away: s.away !== null && s.away !== undefined ? String(s.away) : ''
    }))

    setScores({
      [match.id]: {
        home: match.score_home ?? '',
        away: match.score_away ?? '',
        sets: editableSets
      }
    })
  }


  const handleDetailsUpdate = async () => {
    if (!editingDetails) return

    try {
      await updateMatch(editingDetails.id, {
        referee: editingDetails.referee,
        field: editingDetails.field,
        match_time: editingDetails.match_time,
        match_date: editingDetails.match_date
      })
      setShowEditDetailsModal(false)
      setShowEditDetailsModal(false)
      setEditingDetails(null)
      alert(t('matches_page.details_saved'))
    } catch (err) {
      alert('Fout: ' + err.message)
    }
  }

  const handleGeneratePreview = () => {
    if (generatorSettings.poule_ids.length === 0) {
      alert(t('matches_page.alert_select_poule'))
      return
    }

    setGeneratorSettings({ ...generatorSettings, generating: true })

    try {
      const relevantPoules = poules.filter(p => generatorSettings.poule_ids.includes(p.id))
      const relevantTeams = teams.filter(t => generatorSettings.poule_ids.includes(t.poule_id))

      if (relevantTeams.length < 2) {
        throw new Error(t('matches_page.alert_too_few_teams'))
      }

      let fieldsToUse = generatorSettings.selected_fields
      if (!fieldsToUse || fieldsToUse.length === 0) {
        fieldsToUse = activeTenant?.fields && activeTenant.fields.length > 0
          ? activeTenant.fields
          : ['Veld 1', 'Veld 2']
      }

      const schedule = generateSmartSchedule(
        relevantPoules,
        relevantTeams,
        {
          startTime: generatorSettings.start_time,
          matchDuration: parseInt(generatorSettings.match_duration),
          breakDuration: parseInt(generatorSettings.break_duration),
          fields: fieldsToUse,
          type: generatorSettings.type
        }
      )

      setPreviewMatches(schedule)
      setGeneratorStep(2)
      soundFx.play('click')

    } catch (err) {
      alert(t('common.gen_error') + err.message)
    } finally {
      setGeneratorSettings({ ...generatorSettings, generating: false })
    }
  }

  const handleConfirmGeneration = async () => {
    setGeneratorSettings({ ...generatorSettings, generating: true })
    try {
      const { error: deleteError } = await supabase
        .from('matches')
        .delete()
        .in('poule_id', generatorSettings.poule_ids)
        .eq('tournament_id', tournamentId)

      if (deleteError) throw deleteError

      const matchesToInsert = previewMatches.map(m => ({
        tournament_id: tournamentId,
        tenant_id: activeTenant.id,
        poule_id: m.poule_id,
        home_team_id: m.home_team_id,
        away_team_id: m.away_team_id,
        round_name: m.round_name || Ronde,
        match_time: m.match_time,
        match_date: null,
        field: m.field,
        referee: null,
        score_home: null,
        score_away: null
      }))

      const { error: insertError } = await supabase
        .from('matches')
        .insert(matchesToInsert)

      if (insertError) throw insertError

      soundFx.play('success')
      setShowGeneratorModal(false)
      setGeneratorStep(1)
      setPreviewMatches([])
      setGeneratorSettings({ ...generatorSettings, generating: false, poule_ids: [] })
      refetch()

    } catch (err) {
      console.error(err)
      alert('Opslaan mislukt: ' + err.message)
      setGeneratorSettings({ ...generatorSettings, generating: false })
    }
  }

  const handleAutoCreatePoule = async () => {
    try {
      setGeneratorSettings({ ...generatorSettings, generating: true })

      // 1. Check or Create Division
      let divId
      const { data: existingDivs } = await supabase.from('divisions').select('id').eq('tournament_id', tournamentId).order('created_at').limit(1)

      if (existingDivs && existingDivs.length > 0) {
        divId = existingDivs[0].id
      } else {
        const { data: newDiv, error: divError } = await supabase.from('divisions')
          .insert({ tournament_id: tournamentId, tenant_id: activeTenant.id, name: 'Algemeen' })
          .select()
          .single()
        if (divError) throw divError
        divId = newDiv.id
      }

      // 2. Create Poule
      const { data: newPoule, error: pouleError } = await supabase.from('poules')
        .insert({ division_id: divId, tenant_id: activeTenant.id, name: 'Poule A' })
        .select()
        .single()

      if (pouleError) throw pouleError

      // 3. Update Teams (Assign all unassigned teams to this new poule)
      if (newPoule) {
        await supabase.from('teams')
          .update({ poule_id: newPoule.id })
          .eq('tournament_id', tournamentId)
          .is('poule_id', null)
      }

      alert('✅ Poule A aangemaakt en teams gekoppeld! De pagina wordt herladen.')
      window.location.reload()

    } catch (err) {
      alert(t('common.gen_error') + err.message)
      setGeneratorSettings({ ...generatorSettings, generating: false })
    }
  }

  const downloadMatchesTemplate = () => {
    const headers = ['Datum', 'Tijd', 'Poule', 'ThuisTeam', 'UitTeam', 'Veld', 'Scheidsrechter']
    const csvContent = headers.join(',')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', 'matches_import_template.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadMatchesCSV = () => {
    if (!matches || matches.length === 0) {
      alert('Geen wedstrijden om te exporteren.')
      return
    }
    const headers = ['Datum', 'Tijd', 'Poule', 'ThuisTeam', 'UitTeam', 'Veld', 'Scheidsrechter']
    const csvContent = [
      headers.join(','),
      ...matches.map(m => {
        const homeName = m.home_team?.name || ''
        const awayName = m.away_team?.name || ''
        const pouleName = poules.find(p => String(p.id) === String(m.poule_id))?.name || ''
        return `"${m.match_date || ''}","${m.match_time || ''}","${pouleName}","${homeName}","${awayName}","${m.field || ''}","${m.referee || ''}"`
      })
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', `matches_export_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleMatchesFileUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const encoding = await detectCsvEncoding(file)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: encoding,
      complete: async (results) => {
        const data = results.data
        if (!data || data.length === 0) {
          alert('Het bestand is leeg of kon niet worden gelezen.')
          return
        }

        setUploading(true)
        let successCount = 0
        let errors = []

        // Map team name to ID, and poule name to ID
        let teamMap = {}
        if (teams) {
          teams.forEach(t => teamMap[t.name.toLowerCase()] = t.id)
        }
        let pouleMap = {}
        if (poules) {
          poules.forEach(p => pouleMap[p.name.toLowerCase()] = p.id)
        }

        for (let i = 0; i < data.length; i++) {
          const row = data[i]
          
          // Fuzzy match headers
          const dateKey = Object.keys(row).find(k => k.toLowerCase() === 'datum' || k.toLowerCase() === 'date')
          const timeKey = Object.keys(row).find(k => k.toLowerCase() === 'tijd' || k.toLowerCase() === 'time')
          const pouleKey = Object.keys(row).find(k => k.toLowerCase() === 'poule' || k.toLowerCase() === 'groep' || k.toLowerCase() === 'group')
          const homeKey = Object.keys(row).find(k => k.toLowerCase() === 'thuisteam' || k.toLowerCase() === 'home' || k.toLowerCase() === 'thuis')
          const awayKey = Object.keys(row).find(k => k.toLowerCase() === 'uitteam' || k.toLowerCase() === 'away' || k.toLowerCase() === 'uit')
          const fieldKey = Object.keys(row).find(k => k.toLowerCase() === 'veld' || k.toLowerCase() === 'pitch' || k.toLowerCase() === 'field')
          const refKey = Object.keys(row).find(k => k.toLowerCase() === 'scheidsrechter' || k.toLowerCase() === 'referee' || k.toLowerCase() === 'ref')

          const dateVal = dateKey ? row[dateKey]?.trim() : ''
          const timeVal = timeKey ? row[timeKey]?.trim() : ''
          const pouleName = pouleKey ? row[pouleKey]?.trim() : ''
          const homeName = homeKey ? row[homeKey]?.trim() : ''
          const awayName = awayKey ? row[awayKey]?.trim() : ''
          const fieldVal = fieldKey ? row[fieldKey]?.trim() : ''
          const refVal = refKey ? row[refKey]?.trim() : ''

          if (homeName && awayName) {
            let homeId = null
            let awayId = null
            let pouleId = null

            // 1. Resolve Poule
            if (pouleName) {
              const normPoule = pouleName.toLowerCase()
              if (pouleMap[normPoule]) {
                pouleId = pouleMap[normPoule]
              } else {
                try {
                  // Pre-fetch/create default division if missing
                  let divId
                  const { data: existingDivs } = await supabase.from('divisions').select('id').eq('tournament_id', tournamentId).limit(1)
                  if (existingDivs && existingDivs.length > 0) {
                    divId = existingDivs[0].id
                  } else {
                    const { data: newDiv, error: divError } = await supabase.from('divisions')
                      .insert({ tournament_id: tournamentId, tenant_id: activeTenant.id, name: 'Algemeen' })
                      .select().single()
                    if (divError) throw divError
                    divId = newDiv.id
                  }

                  const { data: newPoule, error: pouleError } = await supabase.from('poules').insert({
                    tournament_id: tournamentId,
                    division_id: divId,
                    tenant_id: activeTenant.id,
                    name: pouleName
                  }).select().single()
                  if (pouleError) throw pouleError
                  pouleId = newPoule.id
                  pouleMap[normPoule] = newPoule.id
                } catch (err) {
                  errors.push(`Regel ${i + 2}: Kon poule '${pouleName}' niet aanmaken.`)
                }
              }
            }

            // 2. Resolve Home Team
            if (homeName) {
              const normHome = homeName.toLowerCase()
              if (teamMap[normHome]) {
                homeId = teamMap[normHome]
              } else {
                try {
                  const { data: newTeam, error: teamError } = await supabase.from('teams').insert({
                    tournament_id: tournamentId,
                    name: homeName,
                    poule_id: pouleId
                  }).select().single()
                  if (teamError) throw teamError
                  homeId = newTeam.id
                  teamMap[normHome] = newTeam.id
                } catch (err) {
                  errors.push(`Regel ${i + 2}: Kon thuis-team '${homeName}' niet aanmaken.`)
                }
              }
            }

            // 3. Resolve Away Team
            if (awayName) {
              const normAway = awayName.toLowerCase()
              if (teamMap[normAway]) {
                awayId = teamMap[normAway]
              } else {
                try {
                  const { data: newTeam, error: teamError } = await supabase.from('teams').insert({
                    tournament_id: tournamentId,
                    name: awayName,
                    poule_id: pouleId
                  }).select().single()
                  if (teamError) throw teamError
                  awayId = newTeam.id
                  teamMap[normAway] = newTeam.id
                } catch (err) {
                  errors.push(`Regel ${i + 2}: Kon uit-team '${awayName}' niet aanmaken.`)
                }
              }
            }

            // 4. Create Match
            try {
              await createMatch({
                home_team_id: homeId,
                away_team_id: awayId,
                poule_id: pouleId,
                referee: refVal,
                field: fieldVal,
                match_time: timeVal || null,
                match_date: dateVal || null
              })
              successCount++
            } catch (err) {
              errors.push(`Regel ${i + 2}: Kon wedstrijd ${homeName} vs ${awayName} niet aanmaken (${err.message})`)
            }
          } else {
            errors.push(`Regel ${i + 2}: ThuisTeam of UitTeam ontbreekt.`)
          }
        }

        setUploading(false)
        alert(t('teams_page.import_success', { count: successCount }) + (errors.length > 0 ? `\n\n${t('common.errors')}:\n${errors.join('\n')}` : ''))
        if (successCount > 0) window.location.reload()
        event.target.value = ''
      },
      error: (err) => {
        alert('Fout tijdens het parsen van CSV: ' + err.message)
      }
    })
  }

  const handleDeleteAllMatches = async () => {
    if (matches.length === 0) return
    if (!confirm('Weet je zeker dat je alle wedstrijden wilt verwijderen?')) return
    const check = prompt('Typ VERWIJDEREN om te bevestigen:')
    if (check !== 'VERWIJDEREN' && check !== 'DELETE') return

    try {
      const { error } = await supabase.from('matches').delete().eq('tournament_id', tournamentId)
      if (error) throw error
      alert('Alle wedstrijden succesvol verwijderd.')
      window.location.reload()
    } catch (err) {
      alert('Fout: ' + err.message)
    }
  }

  const getScoreBadgeColor = (homeScore, awayScore, isHome) => {
    if (homeScore === null || awayScore === null) return 'bg-gray-400'

    if (isHome) {
      if (homeScore > awayScore) return 'bg-green-500'
      if (homeScore < awayScore) return 'bg-red-500'
      return 'bg-orange-500'
    } else {
      if (awayScore > homeScore) return 'bg-green-500'
      if (awayScore < homeScore) return 'bg-red-500'
      return 'bg-orange-500'
    }
  }

  return (
    <div className="min-h-screen">
      <div className="premium-container">
        {/* Hero Section */}
        <div className="mb-12 animate-slideUp">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4">
            <ArrowLeft size={16} className="mr-2" />
            {t('matches_page.back_dashboard')}
          </Button>

          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-400 to-purple-600 shadow-lg">
                <Calendar className="text-white" size={32} />
              </div>
              <div>
                <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                  {t('matches_page.title')}
                </h1>
                <p className="text-lg text-gray-600 mt-1">{t('matches_page.subtitle')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <DarkModeToggleCompact />
              <Button onClick={() => setShowReportModal(true)} variant="secondary" icon={<FileText size={18} />}>
                {t('matches_page.round_report_btn')}
              </Button>

              {!isKnockout ? (
                <Button
                  onClick={() => {
                    if (!setupStatus.isReady) {
                      alert(`${t('common.setup_incomplete_alert')}\n\n${setupStatus.blocker?.label || 'Complete setup first'}`)
                      return
                    }
                    setShowGeneratorModal(true)
                  }}
                  variant="secondary"
                  icon={<Zap size={18} />}
                  className={!setupStatus.isReady ? 'opacity-50 cursor-not-allowed' : ''}
                  title={!setupStatus.isReady ? setupStatus.blocker?.label : ''}
                >
                  {t('matches_page.auto_generate_btn')}
                </Button>
              ) : (
                <Button onClick={() => navigate(`/admin/tournaments/${tournamentId}/bracket`)} variant="secondary" icon={<Zap size={18} />}>
                  {t('matches_page.to_brackets_btn')}
                </Button>
              )}

              <Button onClick={() => setShowModal(true)} variant="primary" icon={<Plus size={20} />}>
                {t('matches_page.new_match_btn')}
              </Button>
            </div>
          </div>
        </div>

        <HelpTip title={`💡 ${t('matches_page.subtitle')}`}>
          <strong>{t('matches_page.helptip_schema_title')}</strong> {t('matches_page.helptip_schema_text')}<br />
          <strong>{t('matches_page.helptip_results_title')}</strong> {t('matches_page.helptip_results_text')}<br />
          <strong>{t('matches_page.helptip_tip_title')}</strong> {t('matches_page.helptip_tip_text')}
        </HelpTip>

        {/* Action Bar */}
        <div className="flex flex-wrap gap-3 items-center glass-strong p-4 rounded-2xl border border-white/30 mb-6">
          <div className="flex gap-2 pr-3 border-r border-white/30">
            {matches.length > 0 && (
              <Button variant="ghost" onClick={handleDeleteAllMatches} size="sm" className="text-red-500 hover:bg-red-50">
                <Trash2 size={16} />
              </Button>
            )}
            <Button variant="ghost" onClick={downloadMatchesTemplate} size="sm" icon={<Download size={16} />}>
              Template
            </Button>
            <label className="cursor-pointer">
              <input type="file" accept=".csv" onChange={handleMatchesFileUpload} className="hidden" />
              <div className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white/20 rounded-lg transition-all cursor-pointer">
                <FileUp size={16} className="mr-2" />
                Import
              </div>
            </label>
            <Button variant="ghost" onClick={downloadMatchesCSV} size="sm" icon={<Upload size={16} className="rotate-180" />}>
              Export
            </Button>
          </div>
        </div>

        {loading || uploading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[var(--accent-primary)] mb-4"></div>
            {uploading && <p className="text-gray-500 animate-pulse">Bezig met uploaden...</p>}
          </div>
        ) : matches.length === 0 ? (
          <div className="glass-strong premium-card rounded-3xl text-center py-20 border border-white/20 shadow-xl max-w-2xl mx-auto mt-6">
            <div className="p-5 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center border border-white/20 shadow-inner">
              <Calendar className="text-[var(--accent-primary)] animate-pulse" size={36} />
            </div>
            <p className="text-gray-900 text-xl font-bold mb-2">{t('matches_page.no_matches_title')}</p>
            <p className="text-gray-500 mb-8 max-w-md mx-auto text-sm leading-relaxed">{isKnockout ? t('matches_page.no_matches_hint_knockout') : t('matches_page.no_matches_hint_poules')}</p>
            {!isKnockout ? (
              <Button
                onClick={() => {
                  if (!setupStatus.isReady) {
                    alert(`${t('common.setup_incomplete_alert')}\n\n${setupStatus.blocker?.label || 'Complete setup first'}`)
                    return
                  }
                  setShowGeneratorModal(true)
                }}
                variant="primary"
                icon={<Sparkles size={20} />}
                className={`bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] text-white hover:opacity-95 shadow-lg shadow-purple-500/20 rounded-xl py-3.5 px-6 font-bold ${!setupStatus.isReady ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t('matches_page.generate_schedule_btn')}
              </Button>
            ) : (
              <Button onClick={() => navigate(`/admin/tournaments/${tournamentId}/bracket`)} variant="primary" icon={<Sparkles size={20} />} className="bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] text-white hover:opacity-95 shadow-lg shadow-purple-500/20 rounded-xl py-3.5 px-6 font-bold">
                {t('matches_page.go_to_brackets_btn')}
              </Button>
            )}
          </div>
        ) : (
          <div className="glass-strong premium-card rounded-2xl">

            {/* Poule Filter Tabs - only show when multiple poules */}
            {poules.length > 1 && (
              <div className="flex gap-2 p-4 pb-0 flex-wrap">
                <button
                  onClick={() => setSelectedPouleFilter('all')}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all tracking-wider uppercase border ${
                    selectedPouleFilter === 'all'
                      ? 'bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] text-white border-transparent shadow-md'
                      : 'bg-white/5 text-gray-700 border-white/10 hover:bg-white/10 hover:text-gray-900'
                  }`}
                >
                  {t('matches_page.filter_all')}
                </button>
                {poules.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPouleFilter(p.id)}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all tracking-wider uppercase border ${
                      selectedPouleFilter === p.id
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-transparent shadow-md'
                        : 'bg-white/5 text-gray-700 border-white/10 hover:bg-white/10 hover:text-gray-900'
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}

            {/* Matches grouped by timeslot */}
            <div className="p-4 space-y-6">
              {(() => {
                const filteredMatches = selectedPouleFilter === 'all'
                  ? matches
                  : matches.filter(m => String(m.poule_id) === String(selectedPouleFilter))

                const timeSlots = {}
                filteredMatches.forEach(m => {
                  const key = m.match_time ? m.match_time.slice(0, 5) : t('common.no_time')
                  if (!timeSlots[key]) timeSlots[key] = []
                  timeSlots[key].push(m)
                })

                const sortedTimes = Object.keys(timeSlots).sort()

                return sortedTimes.map(time => (
                  <div key={time}>
                    {/* Timeslot header */}
                    <div className="flex items-center gap-4 mb-4 mt-6 first:mt-2">
                      <span className="text-2xl font-black text-gray-900 bg-gradient-to-r from-gray-950 to-gray-700 bg-clip-text text-transparent">{time}</span>
                      <div className="flex-1 h-[2px] bg-gradient-to-r from-white/30 to-transparent"></div>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/10 text-gray-600 border border-white/20 uppercase tracking-wider">{timeSlots[time].length} {t(timeSlots[time].length === 1 ? 'matches_page.match_count_one' : 'matches_page.match_count_other', { count: '' }).trim()}</span>
                    </div>
                    {/* Matches grid: 2 columns when multiple matches at same time */}
                    <div className={`grid gap-3 ${timeSlots[time].length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                      {timeSlots[time].map(match => {
                        const isEditing = editingMatch === match.id
                        const homeTeam = match.home_team?.name || (match.home_team_id ? t('common.unknown') : t('common.bye'))
                        const awayTeam = match.away_team?.name || (match.away_team_id ? t('common.unknown') : t('common.bye'))
                        const homeScore = match.score_home
                        const awayScore = match.score_away
                        const pouleName = poules.find(p => String(p.id) === String(match.poule_id))?.name

                        return (
                          <div key={match.id} className="glass-subtle rounded-xl border border-white/30 hover:border-[var(--accent-primary)]/40 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 relative group overflow-hidden">
                            {/* Poule color left accent */}
                            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-blue-400 opacity-60" />

                            {/* Match meta: poule badge + field + edit/delete */}
                            <div className="flex justify-between items-center mb-3 px-4 pt-3">
                              <div className="flex items-center gap-2 text-xs flex-wrap">
                                {pouleName && (
                                  <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
                                    {pouleName}
                                  </span>
                                )}
                                {(match.field || isEditing) && (
                                  <span className="flex items-center gap-1 text-gray-400">
                                    <MapPin size={11} />
                                    {isEditing ? (
                                      <input
                                        className="border-b border-gray-300 w-16 focus:outline-none focus:border-blue-500 text-xs"
                                        placeholder={t('matches_page.field_placeholder')}
                                        value={editingDetails?.id === match.id ? editingDetails.field : (match.field || '')}
                                        onChange={(e) => setEditingDetails({ ...editingDetails, field: e.target.value })}
                                      />
                                    ) : match.field}
                                  </span>
                                )}
                                {(match.referee || isEditing) && (
                                  <span className="flex items-center gap-1 text-gray-400">
                                    <User size={11} />
                                    {isEditing ? (
                                      <input
                                        className="border-b border-gray-300 w-20 focus:outline-none focus:border-blue-500 text-xs"
                                        placeholder={t('matches_page.referee_placeholder')}
                                        value={editingDetails?.id === match.id ? editingDetails.referee : (match.referee || '')}
                                        onChange={(e) => setEditingDetails({ ...editingDetails, referee: e.target.value })}
                                      />
                                    ) : match.referee}
                                  </span>
                                )}
                                {match.match_date && !isEditing && (
                                  <span className="text-gray-300">
                                    {new Date(match.match_date).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
                                  </span>
                                )}
                                {isEditing && (
                                  <input
                                    type="date"
                                    className="text-xs border border-gray-200 rounded p-0.5"
                                    value={editingDetails?.id === match.id ? (editingDetails.match_date || '') : (match.match_date || '')}
                                    onChange={(e) => setEditingDetails({ ...editingDetails, match_date: e.target.value })}
                                  />
                                )}
                                {isEditing && (
                                  <input
                                    type="time"
                                    className="text-xs border border-gray-200 rounded p-0.5"
                                    value={editingDetails?.id === match.id ? (editingDetails.match_time || '') : (match.match_time || '')}
                                    onChange={(e) => setEditingDetails({ ...editingDetails, match_time: e.target.value })}
                                  />
                                )}
                              </div>
                              <div className="flex gap-1 shrink-0">
                                <button
                                  onClick={() => {
                                    if (isEditing) {
                                      handleScoreUpdate(match.id)
                                      if (editingDetails && editingDetails.id === match.id) {
                                        updateMatch(match.id, {
                                          match_time: editingDetails.match_time,
                                          match_date: editingDetails.match_date,
                                          field: editingDetails.field,
                                          referee: editingDetails.referee,
                                          home_team_id: editingDetails.home_team_id || null,
                                          away_team_id: editingDetails.away_team_id || null
                                        })
                                      }
                                    } else {
                                      startEditing(match)
                                      setEditingDetails({
                                        id: match.id,
                                        referee: match.referee || '',
                                        field: match.field || '',
                                        match_time: match.match_time || '',
                                        match_date: match.match_date || '',
                                        home_team_id: match.home_team_id,
                                        away_team_id: match.away_team_id
                                      })
                                    }
                                  }}
                                  className={`p-1.5 rounded-lg transition-colors ${isEditing ? 'bg-green-100 text-green-600' : 'text-gray-300 hover:text-blue-500 hover:bg-blue-50'}`}
                                >
                                  {isEditing ? <Check size={16} /> : <Edit2 size={16} />}
                                </button>
                                <button
                                  onClick={() => { if (confirm(t('matches_page.delete_confirm'))) deleteMatch(match.id) }}
                                  className="p-1.5 rounded-lg text-gray-200 hover:text-red-500 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>

                            {/* Teams & Scores */}
                            <div className="flex flex-col gap-2 px-4 pb-3">
                              {/* Home */}
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <TeamAvatar name={match.home_team?.name || homeTeam} logoUrl={match.home_team?.logo_url} size="sm" />
                                  {isEditing ? (
                                    <select
                                      className="text-sm font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded p-1 outline-none w-full max-w-[180px]"
                                      value={editingDetails?.home_team_id || match.home_team_id || ''}
                                      onChange={(e) => setEditingDetails({ ...editingDetails, home_team_id: e.target.value })}
                                    >
                                      <option value="">{t('common.no_team')}</option>
                                      {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                  ) : (
                                    <span className="font-semibold text-gray-800 truncate">{homeTeam}</span>
                                  )}
                                </div>
                                 {isEditing ? (
                                   <input
                                     type="number"
                                     className="w-10 h-10 flex-shrink-0 ml-2 text-center bg-white text-gray-900 font-bold border-2 border-blue-400 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                     value={scores[match.id]?.home ?? ''}
                                     onChange={(e) => setScores({ ...scores, [match.id]: { ...scores[match.id], home: e.target.value } })}
                                   />
                                 ) : (
                                   // Score badge: green glow for winner, red for loser, yellow for draw
                                   <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl text-white font-bold text-sm ${homeScore !== null
                                     ? homeScore > awayScore
                                       ? 'bg-green-500 shadow-md shadow-green-200'
                                       : homeScore < awayScore
                                         ? 'bg-red-400'
                                         : 'bg-amber-400' // draw
                                     : 'bg-gray-200 text-gray-400'
                                     }`}>
                                     {homeScore ?? '-'}
                                   </div>
                                 )}
                               </div>
                               {/* Away */}
                               <div className="flex justify-between items-center">
                                 <div className="flex items-center gap-2 flex-1 min-w-0">
                                   <TeamAvatar name={match.away_team?.name || awayTeam} logoUrl={match.away_team?.logo_url} size="sm" />
                                   {isEditing ? (
                                     <select
                                       className="text-sm font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded p-1 outline-none w-full max-w-[180px]"
                                       value={editingDetails?.away_team_id || match.away_team_id || ''}
                                       onChange={(e) => setEditingDetails({ ...editingDetails, away_team_id: e.target.value })}
                                     >
                                       <option value="">{t('common.no_team')}</option>
                                       {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                     </select>
                                   ) : (
                                     <span className="font-semibold text-gray-800 truncate">{awayTeam}</span>
                                   )}
                                 </div>
                                 {isEditing ? (
                                   <input
                                     type="number"
                                     className="w-10 h-10 flex-shrink-0 ml-2 text-center bg-white text-gray-900 font-bold border-2 border-blue-400 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                     value={scores[match.id]?.away ?? ''}
                                     onChange={(e) => setScores({ ...scores, [match.id]: { ...scores[match.id], away: e.target.value } })}
                                   />
                                 ) : (
                                   <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl text-white font-bold text-sm ${awayScore !== null
                                     ? awayScore > homeScore
                                       ? 'bg-green-500 shadow-md shadow-green-200'
                                       : awayScore < homeScore
                                         ? 'bg-red-400'
                                         : 'bg-amber-400' // draw
                                     : 'bg-gray-200 text-gray-400'
                                     }`}>
                                     {awayScore ?? '-'}
                                   </div>
                                 )}
                               </div>
 
                               {isEditing && sportType === 'sets' && (
                                 <div className="mt-2 border-t border-gray-100 pt-2">
                                   <button
                                     type="button"
                                     onClick={() => setShowSetsTable({ ...showSetsTable, [match.id]: !showSetsTable[match.id] })}
                                     className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 py-1.5 px-2.5 rounded-lg bg-blue-50/50 hover:bg-blue-50 border border-blue-100 transition-all w-full justify-center"
                                   >
                                     🎾 {showSetsTable[match.id] || (Array.isArray(match.set_scores) && match.set_scores.length > 0) ? 'Set-scores verbergen' : 'Set-scores invoeren (optioneel)'}
                                   </button>
                                   {(showSetsTable[match.id] || (Array.isArray(match.set_scores) && match.set_scores.length > 0)) && (
                                     <div className="mt-2 animate-fadeIn">
                                       <SetScoreInput
                                         initialSets={scores[match.id]?.sets}
                                         homeName={homeTeam}
                                         awayName={awayTeam}
                                         onChange={(data) => {
                                           setScores({
                                             ...scores,
                                             [match.id]: {
                                               ...scores[match.id],
                                               sets: data.set_scores,
                                               home: data.score_home ?? '',
                                               away: data.score_away ?? ''
                                             }
                                           })
                                         }}
                                       />
                                     </div>
                                   )}
                                 </div>
                               )}
                            </div>

                            {/* AI Verslag - subtle ghost link */}
                            {homeScore !== null && !isEditing && (
                              <div className="mt-2 pt-2 border-t border-gray-100 px-4 pb-2">
                                <button
                                  onClick={() => setAiReportMatch(match)}
                                  className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-purple-500 transition-colors"
                                >
                                  <Sparkles size={11} />
                                  {t('matches_page.ai_report_btn')}
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Manual Create Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={t('matches_page.modal_create_title')}>
        <form onSubmit={handleCreate} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('matches_page.date_label')}
              type="date"
              value={newMatch.match_date}
              onChange={(e) => setNewMatch({ ...newMatch, match_date: e.target.value })}
            />
            <Input
              label={t('matches_page.time_label')}
              type="time"
              value={newMatch.match_time}
              onChange={(e) => setNewMatch({ ...newMatch, match_time: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('teams_page.input_poule_label')}</label>
            <select
              className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
              value={newMatch.poule_id}
              onChange={(e) => setNewMatch({ ...newMatch, poule_id: e.target.value })}
              required
            >
              <option value="">{t('teams_page.select_poule_placeholder')}</option>
              {poules.map(poule => <option key={poule.id} value={poule.id}>{poule.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('matches_page.home_team_label')}</label>
              <select
                className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                value={newMatch.home_team_id}
                onChange={(e) => setNewMatch({ ...newMatch, home_team_id: e.target.value })}
                required
              >
                <option value="">{t('matches_page.select_team_placeholder') || 'Select...'}</option>
                {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('matches_page.away_team_label')}</label>
              <select
                className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                value={newMatch.away_team_id}
                onChange={(e) => setNewMatch({ ...newMatch, away_team_id: e.target.value })}
                required
              >
                <option value="">{t('matches_page.select_team_placeholder')}</option>
                {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('matches_page.referee_label')}
              value={newMatch.referee}
              onChange={(e) => setNewMatch({ ...newMatch, referee: e.target.value })}
              placeholder={t('matches_page.referee_placeholder_example')}
            />
            <Input
              label={t('matches_page.field_label')}
              value={newMatch.field}
              onChange={(e) => setNewMatch({ ...newMatch, field: e.target.value })}
              placeholder={t('matches_page.field_placeholder_example')}
            />
          </div>

          <Button type="submit" className="w-full">
            <Plus size={20} className="mr-2" />
            {t('matches_page.create_match_submit')}
          </Button>
        </form>
      </Modal>

      {/* Auto Generator Modal (Wizard v2) */}
      <Modal isOpen={showGeneratorModal} onClose={() => setShowGeneratorModal(false)} title={t('matches_page.modal_generate_title')}>
        {generatorStep === 1 ? (
          <div className="space-y-6">
            {/* STAP 1: Instellingen */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('matches_page.step1_title')}</label>
              <div className="max-h-40 overflow-y-auto border rounded-xl p-2 bg-gray-50 space-y-1">
                {poules.length > 0 ? poules.map(poule => (
                  <label key={poule.id} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-100 cursor-pointer hover:border-blue-300">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      checked={generatorSettings.poule_ids.includes(poule.id)}
                      onChange={(e) => {
                        const newIds = e.target.checked
                          ? [...generatorSettings.poule_ids, poule.id]
                          : generatorSettings.poule_ids.filter(id => id !== poule.id)
                        setGeneratorSettings({ ...generatorSettings, poule_ids: newIds })
                      }}
                    />
                    <span className="font-medium">{poule.name}</span>
                  </label>
                )) : (
                  <div className="p-4 text-center text-gray-500 text-sm">{t('matches_page.no_poules_found')}</div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('matches_page.step2_title')}</label>
              <div className="grid grid-cols-2 gap-3">
                <label className={`flex items-center justify-center p-3 border-2 rounded-xl cursor-pointer transition-all ${generatorSettings.type === 'single' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" value="single" className="hidden" checked={generatorSettings.type === 'single'} onChange={() => setGeneratorSettings({ ...generatorSettings, type: 'single' })} />
                  <span className="font-bold">{t('matches_page.type_single')}</span>
                </label>
                <label className={`flex items-center justify-center p-3 border-2 rounded-xl cursor-pointer transition-all ${generatorSettings.type === 'double' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" value="double" className="hidden" checked={generatorSettings.type === 'double'} onChange={() => setGeneratorSettings({ ...generatorSettings, type: 'double' })} />
                  <span className="font-bold">{t('matches_page.type_double')}</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Input label={t('matches_page.start_time_label')} type="time" value={generatorSettings.start_time} onChange={(e) => setGeneratorSettings({ ...generatorSettings, start_time: e.target.value })} />
              </div>
              <div>
                <Input label={t('matches_page.match_duration_label')} type="number" value={generatorSettings.match_duration} onChange={(e) => setGeneratorSettings({ ...generatorSettings, match_duration: e.target.value })} />
              </div>
              <div>
                <Input label={t('matches_page.break_duration_label')} type="number" value={generatorSettings.break_duration} onChange={(e) => setGeneratorSettings({ ...generatorSettings, break_duration: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('matches_page.step3_title')}</label>
              <div className="flex flex-wrap gap-2 items-center">
                {activeTenant?.fields && activeTenant.fields.length > 0 ? activeTenant.fields.map(field => (
                  <label key={field} className={`px-3 py-1.5 rounded-full text-sm font-medium border cursor-pointer transition-all ${generatorSettings.selected_fields.includes(field) ? 'bg-green-100 text-green-700 border-green-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={generatorSettings.selected_fields.includes(field)}
                      onChange={(e) => {
                        const newFields = e.target.checked
                          ? [...generatorSettings.selected_fields, field]
                          : generatorSettings.selected_fields.filter(f => f !== field)
                        setGeneratorSettings({ ...generatorSettings, selected_fields: newFields })
                      }}
                    />
                    {field}
                  </label>
                )) : (
                  <div className="text-xs text-gray-500 italic">{t('matches_page.no_fields_hint')}</div>
                )}

                {/* Quick Add Button */}
                {isAddingField ? (
                  <div className="flex items-center gap-1 animate-fadeIn">
                    <input
                      autoFocus
                      className="px-2 py-1 text-sm border rounded-lg w-24 focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder={t('matches_page.field_name_placeholder')}
                      value={newFieldInput}
                      onChange={(e) => setNewFieldInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNewField()}
                    />
                    <button onClick={handleAddNewField} className="bg-green-600 text-white rounded-md p-1 hover:bg-green-700"><Check size={14} /></button>
                    <button onClick={() => setIsAddingField(false)} className="text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAddingField(true)}
                    className="px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-gray-300 text-gray-500 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center gap-1"
                  >
                    <Plus size={14} /> {t('matches_page.new_field_btn')}
                  </button>
                )}
              </div>
            </div>

            <Button
              onClick={handleGeneratePreview}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
              disabled={generatorSettings.generating}
            >
              {generatorSettings.generating ? t('common.calculating') : t('matches_page.step2_btn')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col h-[500px]">
            {/* STAP 2: Preview */}
            <div className="flex-1 overflow-y-auto border rounded-xl bg-gray-50 p-4 mb-4">
              {previewMatches.length > 0 ? (
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2">{t('matches_page.time_label')}</th>
                      <th className="px-3 py-2">{t('matches_page.field_placeholder')}</th>
                      <th className="px-3 py-2">{t('matches_page.title')}</th>
                      <th className="px-3 py-2">{t('teams_page.input_poule_label')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {previewMatches.map((m, idx) => {
                      const home = teams.find(t => t.id === m.home_team_id)?.name || '?'
                      const away = teams.find(t => t.id === m.away_team_id)?.name || '?'
                      const pouleName = poules.find(p => p.id === m.poule_id)?.name || '?'

                      // Conflict Detection: Check previous matches in this list
                      // Simple check: Did this team play in the immediately preceding slot?
                      // Note: smartScheduler already tries to avoid this, so if it happens, it's a forced fit.
                      let warning = null

                      // We look at the generated schedule so far (0 to idx-1)
                      // This is a simple visual check for the user
                      const prevMatchHome = previewMatches.slice(0, idx).reverse().find(pm => pm.home_team_id === m.home_team_id || pm.away_team_id === m.home_team_id)
                      const prevMatchAway = previewMatches.slice(0, idx).reverse().find(pm => pm.home_team_id === m.away_team_id || pm.away_team_id === m.away_team_id)

                      const isBackToBack = (prevMatch, currentMatch) => {
                        if (!prevMatch) return false
                        // Calculate end time of prev match
                        const [h, min] = prevMatch.match_time.split(':').map(Number)
                        const prevEndInMinutes = h * 60 + min + parseInt(generatorSettings.match_duration) + parseInt(generatorSettings.break_duration)

                        // Calculate start of current
                        const [h2, min2] = currentMatch.match_time.split(':').map(Number)
                        const currentStartInMinutes = h2 * 60 + min2

                        // If start is BEFORE end of break, it's tight.
                        return currentStartInMinutes < prevEndInMinutes
                      }

                      if (isBackToBack(prevMatchHome, m) || isBackToBack(prevMatchAway, m)) {
                        warning = t('matches_page.no_rest_warning')
                      }

                      return (
                        <tr key={idx} className={`bg-white ${warning ? 'bg-orange-50' : ''}`}>
                          <td className="px-3 py-2 font-mono font-medium">
                            {m.match_time.slice(0, 5)}
                            {warning && <span className="block text-xs text-orange-600 font-bold">{warning}</span>}
                          </td>
                          <td className="px-3 py-2">{m.field}</td>
                          <td className="px-3 py-2">
                            <span className={isBackToBack(prevMatchHome, m) ? "font-bold text-orange-700" : ""}>{home}</span>
                            <span className="mx-1">-</span>
                            <span className={isBackToBack(prevMatchAway, m) ? "font-bold text-orange-700" : ""}>{away}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">{pouleName}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">{t('matches_page.no_matches_calculated')}</div>
              )}
            </div>

            <div className="border-t pt-4 flex gap-3">
              <Button variant="ghost" onClick={() => setGeneratorStep(1)}>
                {t('common.back')}
              </Button>
              <Button
                onClick={handleConfirmGeneration}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                disabled={generatorSettings.generating}
              >
                {generatorSettings.generating ? t('common.saving') : t('matches_page.confirm_save_btn')}
              </Button>
            </div>

            <p className="text-xs text-center text-gray-400 mt-2">
              {t('matches_page.overwrite_warning', { count: previewMatches.length })}
            </p>
          </div>
        )}
      </Modal>

      {/* Edit Details Modal */}
      <Modal isOpen={showEditDetailsModal} onClose={() => setShowEditDetailsModal(false)} title={t('matches_page.modal_edit_details_title')}>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('matches_page.date_label')}
              type="date"
              value={editingDetails?.match_date || ''}
              onChange={(e) => setEditingDetails({ ...editingDetails, match_date: e.target.value })}
            />
            <Input
              label={t('matches_page.time_label')}
              type="time"
              value={editingDetails?.match_time || ''}
              onChange={(e) => setEditingDetails({ ...editingDetails, match_time: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('matches_page.referee_label')}</label>
              {activeTenant?.referees && activeTenant.referees.length > 0 ? (
                <select
                  className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                  value={editingDetails?.referee || ''}
                  onChange={(e) => setEditingDetails({ ...editingDetails, referee: e.target.value })}
                >
                  <option value="">{t('matches_page.kies_referee')}</option>
                  {activeTenant.referees.map((ref, i) => (
                    <option key={i} value={ref}>{ref}</option>
                  ))}
                </select>
              ) : (
                <Input
                  value={editingDetails?.referee || ''}
                  onChange={(e) => setEditingDetails({ ...editingDetails, referee: e.target.value })}
                  placeholder={t('matches_page.referee_placeholder_example')}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('matches_page.field_label')}</label>
              {activeTenant?.fields && activeTenant.fields.length > 0 ? (
                <select
                  className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                  value={editingDetails?.field || ''}
                  onChange={(e) => setEditingDetails({ ...editingDetails, field: e.target.value })}
                >
                  <option value="">{t('matches_page.kies_field')}</option>
                  {activeTenant.fields.map((field, i) => (
                    <option key={i} value={field}>{field}</option>
                  ))}
                </select>
              ) : (
                <Input
                  value={editingDetails?.field || ''}
                  onChange={(e) => setEditingDetails({ ...editingDetails, field: e.target.value })}
                  placeholder={t('matches_page.field_placeholder_example')}
                />
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleDetailsUpdate} className="flex-1">
              {t('common.save')}
            </Button>
            <Button variant="ghost" onClick={() => setShowEditDetailsModal(false)} className="flex-1">
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* NEW: Round Report Modal */}
      <Modal isOpen={showReportModal} onClose={() => setShowReportModal(false)} title={t('matches_page.round_report_modal_title')} size="lg">
        <AIMatchReportGenerator matches={matches} teams={teams} sportType={sportType} />
      </Modal>

      {/* AI Report Modal (Single Match) */}
      <AIReportModal
        match={aiReportMatch}
        isOpen={!!aiReportMatch}
        onClose={() => setAiReportMatch(null)}
      />
    </div >
  )
}

// --- AI Report Component (Updated with REAL Gemini) ---
const AIReportModal = ({ match, isOpen, onClose }) => {
  const { t } = useTranslation()
  const [report, setReport] = useState('')
  const [generating, setGenerating] = useState(false)

  const homeName = match?.home_team?.name || t('matches_page.thuisclub')
  const awayName = match?.away_team?.name || t('matches_page.uitclub')
  const scoreHome = match?.score_home
  const scoreAway = match?.score_away
  const referee = match?.referee || t('matches_page.default_referee')

  useEffect(() => {
    if (isOpen && match) {
      generateAIReport()
    }
  }, [isOpen, match])

  const generateAIReport = async () => {
    setGenerating(true)
    setReport('')

    try {
      const prompt = `
        ${t('matches_page.prompt_instruction')}
        ${t('matches_page.prompt_match')}: ${homeName} ${t('matches_page.prompt_tegen')} ${awayName}.
        ${t('matches_page.prompt_result')}: ${scoreHome}-${scoreAway}.
        ${t('matches_page.prompt_referee')}: ${referee}.
        ${t('matches_page.prompt_field')}: ${match?.field || t('matches_page.default_field')}.
        
        ${t('matches_page.prompt_style')}
        ${t('matches_page.prompt_emojis')}
        `

      // Simuleer een kleine vertraging voor UX als de API te snel is, maar wacht op ECHTE response
      // const delay = new Promise(r => setTimeout(r, 1000));
      const [text] = await Promise.all([
        askGemini(prompt),
        // delay
      ]);

      setReport(text);

    } catch (err) {
      console.error(err);
      setReport(t('matches_page.report_error'));
    } finally {
      setGenerating(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('matches_page.ai_report_title')}>
      <div className="space-y-6">
        {generating ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-purple-500/30 blur-xl rounded-full animate-pulse" />
              <Sparkles className="text-purple-600 animate-spin relative" size={48} />
            </div>
            <p className="text-purple-700 font-extrabold tracking-wide animate-pulse uppercase text-xs">{t('matches_page.ai_writing')}</p>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/5 border border-purple-500/20 p-6 rounded-2xl shadow-inner relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-6 -mr-6 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />
            <div className="flex items-start gap-4">
              <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 p-3 rounded-full shrink-0 border border-purple-500/25">
                <Zap className="text-purple-600" size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-black text-gray-900 text-lg mb-2">{homeName} vs {awayName}</h3>
                <div className="text-gray-700 leading-relaxed font-semibold text-sm whitespace-pre-line">
                  {report}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-white/10 pt-4">
          {!generating && (
            <Button onClick={generateAIReport} className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-500/10">
              <Sparkles size={16} className="mr-2" />
              {t('matches_page.regenerate_btn')}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={generating}>{t('matches_page.close_btn')}</Button>
        </div>
      </div>
    </Modal>
  )
}

